import { curbAreaMode, curbZoneGeoJsonLayerModes } from "@/modules/curb-zone/curb-zone-map.const";
import { CreateCurbAreaInput, UpdateCurbAreaInput } from "@/types/gql/graphql";
import { EditableGeoJsonLayer, EditAction } from "@deck.gl-community/editable-layers";
import { CityContext } from "contexts/city-context-provider";
import { PickingInfo } from "deck.gl";
import { FeatureCollection, Polygon } from "geojson";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { filterFeatureIndex } from "helper/map-utils";
import useToggle from "hooks/use-toggle";
import { CurbArea } from "types/curb-area.type";
import { CurbAreaProperties } from "../components/curb-area/modify-curb-area-prompt";
import { CurbZoneMapProps, CurbZonePageModeType } from "../curb-zone.type";
import { useQueryCurbArea } from "./use-query-curb-area";

type UseCurbAreaParams = Pick<CurbZoneMapProps, "appMode">;

export const useCurbArea = ({ appMode }: UseCurbAreaParams) => {
  const { currentCity } = useContext(CityContext);
  const [openCurbAreaModal, setOpenCurbAreaModal] = useToggle(false);
  const [curbAreaEditionData, setCurbAreaEditionData] = useState<FeatureCollection>({
    type: "FeatureCollection",
    features: [],
  });
  const [curbAreaDetail, setCurbAreaDetail] = useState<CurbArea>();
  const { curbAreas, loading: curbAreaLoading, handleUpdateCurbArea, handleAddCurbArea } = useQueryCurbArea();
  const currentEditCurbAreaLayer = useRef<CurbArea>();
  const curbAreaLayerIndexes = useMemo(
    () => filterFeatureIndex(curbAreaEditionData, curbAreaDetail),
    [curbAreaEditionData, curbAreaDetail],
  );

  const showCurbAreaDetail = useMemo(
    () => appMode === CurbZonePageModeType.EditCurbArea && curbAreaDetail && Object.keys(curbAreaDetail).length > 0,
    [appMode, curbAreaDetail],
  );

  const resetDataOfEditState = () => {
    setCurbAreaDetail(undefined);
    currentEditCurbAreaLayer.current = Object.assign({});
  };

  /**
   * If a user clicks outside a curb-area, deselect a curb-area
   */
  const handleSelectCurbArea = (info: PickingInfo) => {
    if (info.layer?.id !== "curb-areas-layer" && curbAreaDetail) {
      resetDataOfEditState();
      return;
    }
  };

  const cancelCurbAreaChange = () => {
    resetDataOfEditState();
  };
  const openAreaModal = async () => {
    setOpenCurbAreaModal();
  };
  const onEditCurbAreaLayer = async ({ updatedData, editType }: EditAction<FeatureCollection<Polygon, CurbArea>>) => {
    setCurbAreaEditionData(updatedData);
    if (editType === "finishMovePosition" || editType === "addFeature") {
      if (appMode === CurbZonePageModeType.AddCurbArea) {
        setOpenCurbAreaModal();
      } else {
        saveCurbAreaProperties();
      }
    }
  };

  const onClickCurbAreaLayer = (info: PickingInfo) => {
    if (appMode !== CurbZonePageModeType.EditCurbArea) {
      return;
    }

    if (info.object) {
      if (info.object && (!info.object.id || info.object.id === curbAreaDetail?.id)) {
        //Click an existed curb-area or remove a point of polygon
        return;
      }
      //Select a curb-area
      setCurbAreaDetail(info.object);
      currentEditCurbAreaLayer.current = info.object;
    }
  };

  const saveCurbAreaProperties = async (promptedProperties?: CurbAreaProperties) => {
    if (appMode === CurbZonePageModeType.AddCurbArea) {
      const lastCurbAreaFeature = curbAreaEditionData.features.at(-1);
      if (!lastCurbAreaFeature) return;
      const createCurbAreaParam = {
        ...promptedProperties,
        cityId: currentCity?.id ?? "",
        geometry: lastCurbAreaFeature.geometry,
      } as CreateCurbAreaInput;
      await handleAddCurbArea(createCurbAreaParam);
    } else {
      if (!curbAreaDetail) return;
      const updateCurbAreaParam: UpdateCurbAreaInput = {
        ...(promptedProperties || curbAreaEditionData.features[curbAreaLayerIndexes[0]]),
        id: curbAreaDetail?.id,
        cityId: currentCity?.id,
        geometry: curbAreaEditionData.features[curbAreaLayerIndexes[0]].geometry,
      };
      //Remove the publishedDate
      if ("publishedDate" in updateCurbAreaParam) {
        delete updateCurbAreaParam.publishedDate;
      }
      if ("type" in updateCurbAreaParam) {
        delete updateCurbAreaParam.type;
      }
      if ("properties" in updateCurbAreaParam) {
        delete updateCurbAreaParam.properties;
      }
      await handleUpdateCurbArea(updateCurbAreaParam);
      if (promptedProperties) {
        resetDataOfEditState();
      }
    }
    if (openCurbAreaModal) {
      setOpenCurbAreaModal();
    }
  };

  const editableGeoJsonLayerMode = useMemo(() => {
    if (curbAreaMode.includes(appMode)) {
      return curbZoneGeoJsonLayerModes[appMode];
    }
    return curbZoneGeoJsonLayerModes[CurbZonePageModeType.View];
  }, [appMode]);

  const editableCurbAreaLayer = new EditableGeoJsonLayer({
    id: "curb-areas-layer",
    data: curbAreaEditionData,
    mode: editableGeoJsonLayerMode,
    selectedFeatureIndexes: curbAreaLayerIndexes,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 1,
    getFillColor: [0, 0, 0, 0],
    getLineColor: [54, 240, 171, 255],
    autoHighlight: curbAreaMode.includes(appMode),
    onEdit: (updatedData) => onEditCurbAreaLayer(updatedData as EditAction<FeatureCollection<Polygon, CurbArea>>),
    onClick: onClickCurbAreaLayer,
    updateTriggers: {
      mode: [appMode],
    },
  });

  const curbAreaLayers = [editableCurbAreaLayer];

  useEffect(() => {
    if (!currentCity || curbAreaLoading) {
      return;
    }
    setCurbAreaEditionData({
      type: "FeatureCollection",
      features: curbAreas,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curbAreas, curbAreaLoading]);

  return {
    openCurbAreaModal,
    setOpenCurbAreaModal,
    curbAreaEditionData,
    setCurbAreaEditionData,
    curbAreaDetail,
    setCurbAreaDetail,
    showCurbAreaDetail,
    curbAreaLayerIndexes,
    openAreaModal,
    cancelCurbAreaChange,
    handleSelectCurbArea,
    curbAreaLayers,
    saveCurbAreaProperties,
    resetDataOfEditState,
  };
};
